import {
  Flex,
  FormControl,
  FormLabel,
  ModalBody,
  ModalContent,
  ModalFooter,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  ChainFullIds,
  ChainIds,
  type FA12Transfer,
  type UnifiedTokenBalance,
  parseChainFullId,
} from "@umami/chains";
import { FormProvider } from "react-hook-form";

import { AddressTile } from "../../AddressTile/AddressTile";
import { AdvancedSettingsAccordion } from "../../AdvancedSettingsAccordion";
import { TokenTile } from "../../AssetTiles";
import { SafeSendTile } from "../../SafeSend/SafeSendTile";
import { SignButton } from "../SignButton";
import { AsyncSignPageFee } from "../SignPageFee";
import { SignPageHeader } from "../SignPageHeader";
import { type SignPageProps, getSender, useSignPageHelpers } from "../utils";

export const SignPage = (props: SignPageProps<{ token: UnifiedTokenBalance }>) => {
  const {
    operations: initialOperations,
    data: { token },
    chainId = ChainFullIds.TEZOS,
  } = props;
  const { fee, operations, estimationFailed, isLoading, form, signer, onSign } = useSignPageHelpers(
    initialOperations,
    chainId
  );
  const hideBalance = useBreakpointValue({ base: true, md: false });
  const fa12Operation = operations.operations[0] as FA12Transfer;
  const { amount, recipient } = fa12Operation;
  const { namespace } = parseChainFullId(chainId);

  const isSafeSendEnabled = form.watch("isSafeSendEnabled");
  const safeSendFee = form.watch("safeSendFee");
  const displayFee = isSafeSendEnabled ? safeSendFee : fee;

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form>
          <SignPageHeader />
          <ModalBody gap="24px">
            <FormControl>
              <FormLabel width="full">Amount</FormLabel>
              <TokenTile amount={amount} token={token} />
              <Flex justifyContent="end" marginTop="12px" paddingX="4px">
                <AsyncSignPageFee chainId={chainId} fee={displayFee} />
              </Flex>
            </FormControl>
            <FormControl>
              <FormLabel width="full">From</FormLabel>
              <AddressTile address={getSender(operations, chainId)} hideBalance={hideBalance} />
            </FormControl>
            <FormControl>
              <FormLabel width="full">To</FormLabel>
              <AddressTile address={recipient} hideBalance={hideBalance} />
            </FormControl>
            {namespace === ChainIds.TEZOS && (
              <SafeSendTile balance={token.balance} operation={fa12Operation} signer={signer} />
            )}
            <AdvancedSettingsAccordion />
          </ModalBody>
          <ModalFooter>
            <SignButton
              isDisabled={estimationFailed}
              isLoading={isLoading}
              onSubmit={onSign}
              signer={signer}
            />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
